import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Archive, ArchiveRestore, Plus } from 'lucide-react';
import { archiveWorkType, createWorkType, restoreWorkType, updateWorkType } from '@mkcore/projects/schema';
import { Badge, Button, Checkbox, EmptyState, Field, Input, PageHeader, Skeleton, Switch, TBody, THead, Table, Td, Th, Tr } from '@mkcore/ui';
import { newIdempotencyKey } from '@/lib/api';
import { useActionMutation } from '@/lib/mutation';
import { useCan } from '@/lib/session';
import { workTypesQuery } from '@/features/projects/queries';

export const Route = createFileRoute('/_app/admin/work-types')({ component: WorkTypes });

/** Company-wide work types with their billable default (CO-0190). */
function WorkTypes() {
  const { t } = useTranslation();
  const canUpdate = useCan('projects:update');
  const [showArchived, setShowArchived] = useState(false);
  const list = useQuery(workTypesQuery(showArchived));
  const [name, setName] = useState('');
  const [billable, setBillable] = useState(true);
  const [key, setKey] = useState(newIdempotencyKey);
  const invalidate = [['project', 'workType']];
  const create = useActionMutation(createWorkType, {
    invalidate,
    onSuccess: () => {
      setName('');
      setBillable(true);
      setKey(newIdempotencyKey());
    },
  });
  const update = useActionMutation(updateWorkType, { invalidate });
  const archive = useActionMutation(archiveWorkType, { invalidate });
  const restore = useActionMutation(restoreWorkType, { invalidate });
  return (
    <>
      <PageHeader eyebrow={t('nav.admin')} title={t('workTypes.title')} description={t('workTypes.description')} />
      {canUpdate && (
        <form
          className="mb-4 flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) create.mutate({ input: { name, billableDefault: billable }, key });
          }}
        >
          <Field label={t('workTypes.new')} className="w-64">
            {(p) => <Input {...p} value={name} onChange={(e) => setName(e.target.value)} placeholder={t('workTypes.placeholder')} />}
          </Field>
          <label className="flex items-center gap-2 pb-2 text-sm">
            <Checkbox checked={billable} onCheckedChange={(v) => setBillable(v === true)} /> {t('workTypes.billable')}
          </label>
          <Button type="submit" loading={create.isPending} disabled={!name.trim()}>
            <Plus /> {t('common.add')}
          </Button>
        </form>
      )}
      <label className="mb-3 flex items-center gap-2 text-sm">
        <Checkbox checked={showArchived} onCheckedChange={(v) => setShowArchived(v === true)} /> {t('org.includeArchived')}
      </label>
      {list.isLoading ? (
        <Skeleton className="h-40" />
      ) : !list.data?.items.length ? (
        <EmptyState title={t('workTypes.empty')} />
      ) : (
        <Table>
          <THead>
            <Tr>
              <Th>{t('common.name')}</Th>
              <Th>{t('workTypes.billable')}</Th>
              <Th className="sr-only">{t('common.actions')}</Th>
            </Tr>
          </THead>
          <TBody>
            {list.data.items.map((w) => (
              <Tr key={w.id}>
                <Td>
                  <span className="font-bold">{w.name}</span>
                  {w.archivedAt && (
                    <Badge tone="neutral" className="ml-2">
                      {t('org.archived')}
                    </Badge>
                  )}
                </Td>
                <Td>
                  <Switch checked={w.billableDefault} disabled={!canUpdate || !!w.archivedAt} aria-label={t('workTypes.billableFor', { name: w.name })} onCheckedChange={(v) => update.mutate({ input: { id: w.id, billableDefault: v }, ifMatch: w.version })} />
                </Td>
                <Td className="text-right">
                  {canUpdate && (
                    <Button size="icon" variant="ghost" aria-label={w.archivedAt ? t('workTypes.restore', { name: w.name }) : t('workTypes.archive', { name: w.name })} onClick={() => (w.archivedAt ? restore.mutate({ input: { id: w.id } }) : archive.mutate({ input: { id: w.id } }))}>
                      {w.archivedAt ? <ArchiveRestore /> : <Archive />}
                    </Button>
                  )}
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
    </>
  );
}
